import { useEffect, useRef } from 'react'
import { RECTANGLE_OPTIONS } from '../constants'
import { boundsToArea, boundsToLiteral } from './areaToBounds'

type UseRectangleDrawArgs = {
  map: google.maps.Map | null
  enabled: boolean
  onComplete: (bounds: google.maps.LatLngBoundsLiteral, area: string) => void
}

export function useRectangleDraw({ map, enabled, onComplete }: UseRectangleDrawArgs) {
  const startRef = useRef<google.maps.LatLng | null>(null)
  const previewRef = useRef<google.maps.Rectangle | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    if (!map || !enabled) return

    const previousDraggable = map.get('draggable')
    map.setOptions({ draggable: false, draggableCursor: 'crosshair' })

    const clearPreview = () => {
      previewRef.current?.setMap(null)
      previewRef.current = null
      startRef.current = null
    }

    const boundsFromCorners = (a: google.maps.LatLng, b: google.maps.LatLng) => {
      const bounds = new google.maps.LatLngBounds()
      bounds.extend(a)
      bounds.extend(b)
      return bounds
    }

    const onMouseDown = (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      startRef.current = e.latLng
      previewRef.current?.setMap(null)
      previewRef.current = new google.maps.Rectangle({
        ...RECTANGLE_OPTIONS,
        map,
        clickable: false,
        editable: false,
        draggable: false,
        bounds: new google.maps.LatLngBounds(e.latLng, e.latLng),
      })
    }

    const onMouseMove = (e: google.maps.MapMouseEvent) => {
      if (!startRef.current || !e.latLng || !previewRef.current) return
      previewRef.current.setBounds(boundsFromCorners(startRef.current, e.latLng))
    }

    const onMouseUp = (e: google.maps.MapMouseEvent) => {
      if (!startRef.current || !e.latLng) {
        clearPreview()
        return
      }

      const bounds = boundsFromCorners(startRef.current, e.latLng)
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const hasArea = ne.lat() !== sw.lat() && ne.lng() !== sw.lng()
      clearPreview()
      if (!hasArea) return

      onCompleteRef.current(boundsToLiteral(bounds), boundsToArea(bounds))
    }

    const downListener = map.addListener('mousedown', onMouseDown)
    const moveListener = map.addListener('mousemove', onMouseMove)
    const upListener = map.addListener('mouseup', onMouseUp)

    return () => {
      downListener.remove()
      moveListener.remove()
      upListener.remove()
      clearPreview()
      map.setOptions({
        draggable: previousDraggable !== false,
        draggableCursor: undefined,
      })
    }
  }, [map, enabled])
}
